import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useState } from 'react';
import CurrencyFormat from 'react-currency-format';
import { toast } from 'react-toastify';
import clsx from 'clsx';
import { useFormik } from 'formik';
import { IPImage } from 'src/components';
import { controlCurrency, valueChange } from 'src/helpers/currencyInput';
import { useStockCodeProductControl } from 'src/services/hooks';
import { ICatalogProduct } from 'src/services/interfaces';
import { useAppDispatch } from 'src/services/redux/app/Store';
import { catalogPriceAdd, setAdded, setError } from 'src/services/redux/slices/products/catalog/catalog-search-slice';
import * as Yup from 'yup';
import StockCodeProductListModal from '../../products/components/modals/stock-code-product-list-modal';

type Props = {
  product: ICatalogProduct;
  isSelected: boolean;
  disableClickToSell?: boolean;
  onToggle: (productId: number) => void;
};

type CurrencyValue = { floatValue: number; formattedValue: string };
type CurrencyFieldValue = CurrencyValue | string;

type FormValues = {
  vendorStockCode: string;
  inventoryQuantity: string;
  price: CurrencyFieldValue;
  comparePrice: CurrencyFieldValue;
};

const STOCK_CODE_EXISTS_MESSAGE = 'Böyle bir stok kodu mevcut';
const EMPTY_CURRENCY = (): CurrencyValue => ({ floatValue: 0, formattedValue: '0,00 ₺' });

const validationSchema = Yup.object({
  vendorStockCode: Yup.string().min(4, 'Minimum 4 karakter').required('Stok kodu zorunlu.'),
  inventoryQuantity: Yup.number()
    .typeError('Stok miktarı sayı olmalıdır.')
    .integer('Tam sayı giriniz.')
    .min(1, 'Stok miktarı en az 1 olmalı.')
    .required('Stok miktarı zorunlu.'),
});

const toFloat = (value: CurrencyFieldValue): number => (typeof value === 'object' && !isNaN(value.floatValue) ? value.floatValue : 0);

const getFormattedCurrency = (value: CurrencyFieldValue): string => (typeof value === 'string' ? value : value.formattedValue || '0,00');

const ProductTableRow: FC<Props> = ({ product, isSelected, disableClickToSell = false, onToggle }) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { getStockCodeProductControl } = useStockCodeProductControl();

  const [showStockCodeModal, setShowStockCodeModal] = useState(false);

  const priceInputId = `best-selling-price-${product.id}`;
  const comparePriceInputId = `best-selling-compare-price-${product.id}`;

  const variantAttributes = product.productAttributes ? Object.entries(product.productAttributes).filter(([, attribute]) => attribute?.categoryAttribute?.isVariant) : [];

  const formik = useFormik<FormValues>({
    initialValues: {
      vendorStockCode: '',
      inventoryQuantity: '',
      price: EMPTY_CURRENCY(),
      comparePrice: EMPTY_CURRENCY(),
    },
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values, helpers) => {
      try {
        if (disableClickToSell) return;

        const isStockCodeAvailable = await verifyStockCode(values.vendorStockCode);
        if (!isStockCodeAvailable) return;

        const response = await dispatch(
          catalogPriceAdd({
            barcode: product.barcode,
            vendorStockCode: values.vendorStockCode,
            inventoryQuantity: parseInt(values.inventoryQuantity),
            price: toFloat(values.price),
            comparePrice: toFloat(values.comparePrice),
          }),
        );

        if (response?.meta?.requestStatus === 'fulfilled') {
          toast.success('Ürün başarıyla onaya gönderildi.');
          queryClient.invalidateQueries(['best-selling-products']);
          dispatch(setAdded(false));
        } else {
          const data = response?.payload?.data;
          toast.error(data?.message || data?.violations?.map((v) => v.title).join('\n') || 'Ürün onaya gönderilemedi.');
          dispatch(setError(false));
        }
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  const verifyStockCode = async (vendorStockCode: string): Promise<boolean> => {
    if (vendorStockCode.length < 4) {
      formik.setFieldError('vendorStockCode', 'Minimum 4 karakter');
      formik.setFieldTouched('vendorStockCode', true, false);
      return false;
    }

    const result = await getStockCodeProductControl(vendorStockCode, [], null);
    if (!result.notFound) {
      formik.setFieldError('vendorStockCode', STOCK_CODE_EXISTS_MESSAGE);
      formik.setFieldTouched('vendorStockCode', true, false);
      setShowStockCodeModal(true);
      return false;
    }

    return true;
  };

  const handleCurrencyChange = (field: 'price' | 'comparePrice', inputId: string, event: { formattedValue: string }) => {
    if (event.formattedValue?.[0] === '0') {
      event.formattedValue = event.formattedValue.replace('0', '');
      controlCurrency(inputId);
    }
    formik.setFieldValue(field, valueChange(event, inputId));
  };

  const isFieldsDisabled = disableClickToSell || formik.isSubmitting;
  const canSubmit = formik.isValid && !isFieldsDisabled;
  const showError = (field: keyof FormValues) => (formik.touched[field] || formik.submitCount > 0) && !!formik.errors[field];

  const FieldError = ({ field }: { field: keyof FormValues }) => (showError(field) ? <div className="fs-8 text-danger mb-n3">{formik.errors[field]}</div> : null);

  return (
    <>
      <tr className={clsx(isSelected && 'bg-light-primary')}>
        <td className="ps-3 sticky-column checkbox">
          <div className="form-check form-check-custom form-check-solid form-check-sm">
            <input className="form-check-input" type="checkbox" checked={isSelected} onChange={() => onToggle(product.id)} aria-label={`${product.title} seç`} />
          </div>
        </td>

        <td className="sticky-column product-name">
          <div className="d-flex align-items-start gap-3">
            <div className="symbol symbol-50px flex-shrink-0">
              <IPImage
                src={product.images?.[0]?.src}
                className="rounded"
                width={75}
                height={75}
                objectFit="contain"
                alt={product.title}
                enablePlaceholderOn={{ loading: true, error: true }}
                loading="lazy"
              />
            </div>
            <div className="d-flex flex-column gap-2">
              <span className="fw-bold text-gray-800">{product.title}</span>
              {variantAttributes.length > 0 && (
                <div className="d-flex flex-wrap gap-1">
                  {variantAttributes.map(([attributeKey, attribute]) => (
                    <span key={attributeKey} className="badge badge-light-primary d-inline-flex align-items-center gap-1">
                      <span className="fw-normal text-muted">{attribute.attributeName}</span>
                      <span className="fw-bolder text-primary">{attribute.attributeValueName}</span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </td>

        <td>
          <span className="text-gray-800">{product.category?.title || '-'}</span>
        </td>

        <td>{product.brand ? product.brand.title : '-'}</td>

        <td className="pretty-wrap text-center">
          <span className="text-gray-800 ">{product.barcode || '-'}</span>
        </td>

        <td>
          <input
            type="text"
            name="vendorStockCode"
            className={clsx('form-control form-control-sm', showError('vendorStockCode') && 'border-danger')}
            placeholder="Satıcı Stok Kodu*"
            value={formik.values.vendorStockCode}
            disabled={isFieldsDisabled}
            onBlur={formik.handleBlur}
            onChange={(e) => formik.setFieldValue('vendorStockCode', e.target.value.trim())}
          />
          <FieldError field="vendorStockCode" />
        </td>

        <td>
          <input
            type="number"
            min="1"
            name="inventoryQuantity"
            className={clsx('form-control form-control-sm', showError('inventoryQuantity') && 'border-danger')}
            placeholder="Stok Miktarı*"
            value={formik.values.inventoryQuantity}
            disabled={isFieldsDisabled}
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            step="1"
          />
          <FieldError field="inventoryQuantity" />
        </td>

        <td>
          <CurrencyFormat
            id={priceInputId}
            className="form-control form-control-sm"
            placeholder="Satış Fiyatı*"
            thousandSeparator="."
            decimalSeparator=","
            suffix=" ₺"
            decimalScale={2}
            disabled={isFieldsDisabled}
            value={getFormattedCurrency(formik.values.price)}
            onFocus={() => controlCurrency(priceInputId)}
            onValueChange={(e) => handleCurrencyChange('price', priceInputId, e)}
          />
        </td>

        <td>
          <CurrencyFormat
            id={comparePriceInputId}
            className="form-control form-control-sm"
            placeholder="Piyasa Satış Fiyatı"
            thousandSeparator="."
            decimalSeparator=","
            suffix=" ₺"
            decimalScale={2}
            disabled={isFieldsDisabled}
            value={getFormattedCurrency(formik.values.comparePrice)}
            onFocus={() => controlCurrency(comparePriceInputId)}
            onValueChange={(e) => handleCurrencyChange('comparePrice', comparePriceInputId, e)}
          />
        </td>

        <td className="text-center">
          <button type="button" className="btn btn-sm btn-light-primary" disabled={!canSubmit} onClick={() => formik.handleSubmit()}>
            {formik.isSubmitting && <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />}
            Tıkla Sat
          </button>
        </td>
      </tr>

      <StockCodeProductListModal show={showStockCodeModal} handleClose={() => setShowStockCodeModal(false)} data={formik.values.vendorStockCode} />
    </>
  );
};

export default ProductTableRow;
